// GhostLight V2 design tokens.
// Source of truth for colors, fonts, spacing, and motion across the app.

const PALETTES = {
  amber: {
    name: 'Ghost Amber',
    accent: '#e8b547',
    accentDim: '#b88f38',
    accentGlow: 'rgba(232, 181, 71, 0.16)',
  },
  ivory: {
    name: 'Paper Lantern',
    accent: '#efe3c7',
    accentDim: '#c8b891',
    accentGlow: 'rgba(239, 227, 199, 0.10)',
  },
  exit: {
    name: 'Exit Sign',
    accent: '#8bc38a',
    accentDim: '#5f9d63',
    accentGlow: 'rgba(139, 195, 138, 0.12)',
  },
};

const DARKNESS = {
  deep:   { ground: '#060403', wing: '#0a0705', house: '#120c08', edge: 'rgba(237, 228, 211, 0.06)' },
  mid:    { ground: '#0e0a08', wing: '#120c09', house: '#1a120d', edge: 'rgba(237, 228, 211, 0.08)' },
  gentle: { ground: '#15100c', wing: '#1a140f', house: '#221811', edge: 'rgba(237, 228, 211, 0.10)' },
};

const TYPE_PAIRS = {
  instrument: {
    display: '"Instrument Serif", "Cormorant Garamond", serif',
    body:    '"Geist", "Inter", system-ui, sans-serif',
    mono:    '"JetBrains Mono", ui-monospace, monospace',
    displayWeight: 400,
    displayTracking: '-0.01em',
  },
  newsreader: {
    display: '"Newsreader", "Lora", serif',
    body:    '"IBM Plex Sans", system-ui, sans-serif',
    mono:    '"IBM Plex Mono", ui-monospace, monospace',
    displayWeight: 400,
    displayTracking: '0',
  },
};

export function buildTokens({ accent = 'amber', darkness = 'mid', type = 'instrument', density = 'comfortable' } = {}) {
  const pal = PALETTES[accent] || PALETTES.amber;
  const d = DARKNESS[darkness] || DARKNESS.mid;
  const t = TYPE_PAIRS[type] || TYPE_PAIRS.instrument;
  const dense = density === 'compact';
  return {
    // color
    ground: d.ground, wing: d.wing, house: d.house,
    edge: d.edge,
    accent: pal.accent, accentDim: pal.accentDim, accentGlow: pal.accentGlow,
    paletteName: pal.name,
    // text
    textHi:  '#ede4d3',
    textMid: 'rgba(237, 228, 211, 0.62)',
    textLow: 'rgba(237, 228, 211, 0.38)',
    textDim: 'rgba(237, 228, 211, 0.22)',
    // semantic
    danger: '#d47e6a',
    calm:   '#8bc38a',
    // type
    fontDisplay: t.display,
    fontBody:    t.body,
    fontMono:    t.body,
    fontCode:    t.mono,
    displayWeight: t.displayWeight,
    displayTracking: t.displayTracking,
    // spacing
    gutter: dense ? 16 : 28,
    stackSm: dense ? 8 : 14,
    stackMd: dense ? 14 : 24,
    stackLg: dense ? 24 : 48,
    stackXl: dense ? 40 : 80,
    // motion
    ease: 'cubic-bezier(0.2, 0.7, 0.1, 1)',
    // type scale
    displayXl: dense ? 28 : 38,
    displayLg: dense ? 22 : 28,
    displayMd: dense ? 17 : 22,
    bodyLg:    dense ? 15 : 17,
    body:      dense ? 13 : 15,
    micro:     12,
    meta:      11,
    // density flag
    dense,
  };
}

export const TYPE_COLOR = {
  'Venting':        '#E47C6B',
  'Advice Needed':  '#E8B64A',
  'Win':            '#7FC46C',
  'Gratitude':      '#C695E0',
  'Question':       '#6BB8DE',
};

export const greenRoomPalette = {
  paper:     '#1a1410',
  paperDeep: '#221b15',
  ink:       '#ece1cb',
  inkMid:    '#c4b9a0',
  inkLow:    '#8a7f6b',
  inkDim:    '#4a4238',
  hair:      '#ffffff0d',
  mark:      '#d9a85c',
  markGlow:  '#d9a85c1a',
};

export function getPulseMessage(pulse) {
  if (!pulse || !pulse.count || pulse.count === 0) {
    return 'The house is still warm.';
  }
  const d = pulse.distribution;
  if (!d || d.length < 5) return 'The house is still warm.';

  const [struggling, fumes, mid, steady, better] = d;
  const total = struggling + fumes + mid + steady + better;
  if (total === 0) return 'The house is still warm.';

  const maxVal = Math.max(struggling, fumes, mid, steady, better);

  if (maxVal === struggling) return 'The company is hurting. Hold space.';
  if (maxVal === fumes)      return 'A lot of us are running on fumes.';
  if (maxVal === mid)        return 'Mostly holding steady backstage.';
  if (maxVal === steady)     return 'Good energy in the house tonight.';
  if (maxVal === better)     return 'The company is feeling strong.';

  return 'The house is still warm.';
}

export const tokens = buildTokens();
export default tokens;
